// 性格档案页:全镇居民的 15 维多维度性格画像(行为特质/动机/价值观)
// 数据源:GET /api/personalities(需登录);展示含每日小波动的快照分数。
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { PersonalityBars, ALL_PERSONALITY_DIMS } from "../personalityDims";

interface PersonalityEntry {
  id: string;
  name: string;
  gender: string;
  isNPC: boolean;
  occupation: string;
  personality: string;
  fame?: number;
  professionCategory?: string;
  personalityScores?: { day: number; base: Record<string, number>; scores: Record<string, number> };
}

const CHAMPION_DIMS: { id: string; label: string; emoji: string }[] = [
  { id: "extraversion", label: "最外向", emoji: "🎉" },
  { id: "romanticism", label: "最浪漫", emoji: "🌙" },
  { id: "conscientiousness", label: "最尽责", emoji: "📋" },
  { id: "val_benevolence", label: "最关爱他人", emoji: "🤝" },
  { id: "mot_achievement", label: "最有成就动机", emoji: "🏆" },
  { id: "mot_affiliation", label: "最重情谊", emoji: "💞" },
  { id: "val_tradition", label: "最恋旧安稳", emoji: "🏡" },
];

export default function Personalities() {
  const nav = useNavigate();
  const [agents, setAgents] = useState<PersonalityEntry[]>([]);
  const [day, setDay] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [showOnly, setShowOnly] = useState<"all" | "npc" | "player">("all");

  const load = useCallback(async () => {
    try {
      const d = await api.personalities();
      setDay(d.day);
      setAgents(d.agents || []);
      setError("");
    } catch (e: any) {
      setError(e?.message || "加载失败,请检查登录状态");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const sorted = [...agents]
    .filter((a) => (showOnly === "all" ? true : showOnly === "npc" ? a.isNPC : !a.isNPC))
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name, "zh");
      const av = a.personalityScores?.scores?.[sortBy] ?? 0;
      const bv = b.personalityScores?.scores?.[sortBy] ?? 0;
      return bv - av;
    });

  const champions = CHAMPION_DIMS.map((c) => {
    const best = [...agents].sort(
      (a, b) => (b.personalityScores?.scores?.[c.id] ?? 0) - (a.personalityScores?.scores?.[c.id] ?? 0)
    )[0];
    return { ...c, best, score: best?.personalityScores?.scores?.[c.id] ?? 0 };
  }).filter((c) => c.best && c.score >= 1);

  return (
    <div className="h-full overflow-y-auto" style={{ background: "#F8F7E5" }}>
      {/* 顶栏 */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3"
        style={{ background: "linear-gradient(90deg,#10173a,#1b2a5e)", color: "#F8F7E5", boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }}>
        <button onClick={() => nav("/town")}
          className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>
          ← 回小镇
        </button>
        <div className="flex-1">
          <div className="text-sm font-black" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            🧬 性格档案
          </div>
          <div className="text-[10px] opacity-70">
            {day ? `第 ${day} 天快照` : "加载中…"} · 15 维 1-100 分 · 基准来自历史表现 + 每日小波动(±5)
          </div>
        </div>
        <button onClick={() => void load()} className="rounded-full px-3 py-1.5 text-xs font-bold hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>
          ↻ 刷新
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* 全镇之最 */}
        {!loading && champions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {champions.map((c) => (
              <div key={c.id} className="rounded-2xl px-3 py-2 text-xs"
                style={{ background: "#fff", border: "1px solid #eee8d0", color: "#4a453a" }}>
                <span className="font-black text-[#b39c4f]">{c.emoji} {c.label}</span>
                <span className="ml-1.5 font-bold">{c.best.name}</span>
                <span className="ml-1 text-[#a9a494]">{c.score}</span>
              </div>
            ))}
          </div>
        )}

        {/* 排序/筛选 */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="rounded-xl px-3 py-1.5 border border-[#ddd5b8] bg-white text-[#4a453a] outline-none">
            <option value="name">按名字</option>
            {ALL_PERSONALITY_DIMS.map((d) => (
              <option key={d.id} value={d.id}>{d.group} · {d.label}</option>
            ))}
          </select>
          <select value={showOnly} onChange={(e) => setShowOnly(e.target.value as any)}
            className="rounded-xl px-3 py-1.5 border border-[#ddd5b8] bg-white text-[#4a453a] outline-none">
            <option value="all">全部居民</option>
            <option value="npc">仅 NPC</option>
            <option value="player">仅玩家/游客</option>
          </select>
          <span className="text-[#a9a494]">{sorted.length} 位居民</span>
        </div>

        {loading && <div className="text-center text-[#a9a494] text-sm py-10">性格档案加载中…</div>}
        {error && <div className="text-center text-[#b91c1c] text-sm py-6">{error}</div>}

        {/* 居民卡片 */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 pb-8">
            {sorted.map((a) => {
              const sc = a.personalityScores?.scores ?? {};
              const vals = ALL_PERSONALITY_DIMS.filter((d) => d.group.includes("价值观"))
                .map((d) => ({ d, v: sc[d.id] ?? 0 }))
                .sort((x, y) => y.v - x.v);
              const top2 = vals.slice(0, 2);
              return (
                <div key={a.id} className="rounded-2xl p-3 space-y-2"
                  style={{ background: "#fff", border: "1px solid #eee8d0", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-sm font-black text-[#1D1D1D]">{a.name}</span>
                      {a.isNPC
                        ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#e8c96a]/30 text-[#8a6d1f]">NPC</span>
                        : <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#dbeafe] text-[#1e40af]">玩家/游客</span>}
                    </div>
                    <div className="text-right space-y-0.5">
                      <div className="text-[10px] text-[#a9a494]">{a.occupation || "居民"}</div>
                      {(a.fame ?? 0) > 0 && (
                        <div className="text-[10px] text-[#b39c4f]">⭐ 声望 {a.fame}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#8a8571] leading-relaxed line-clamp-2">{a.personality || "——"}</div>
                  {top2.length > 0 && (
                    <div className="text-[10px] text-[#b39c4f]">
                      看重:{top2.map((t) => `${t.d.label} ${t.v}`).join(" · ")}
                    </div>
                  )}
                  <PersonalityBars scores={sc} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
